"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.InlineEdit = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const FieldHistory_1 = require("./FieldHistory");
const Toast_1 = require("./Toast");
const toDraft = (value, type) => {
    if (value === null || value === undefined)
        return '';
    const s = String(value);
    // ISO timestamps → yyyy-mm-dd for <input type="date">
    if (type === 'date')
        return s.split('T')[0];
    return s;
};
const defaultFormat = (value, type, options) => {
    if (type === 'money') {
        const n = typeof value === 'number' ? value : parseFloat(String(value));
        return Number.isFinite(n) ? `$${n.toFixed(2)}` : String(value);
    }
    if (type === 'select') {
        return options?.find((o) => o.value === String(value))?.label ?? String(value);
    }
    if (type === 'date')
        return toDraft(value, type);
    return String(value);
};
const displayStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: 32,
    padding: '2px 4px',
    margin: '0 -4px',
    borderRadius: 4,
    borderBottom: '1px dashed rgba(128,128,128,0.6)',
    cursor: 'text',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
};
const baseInputStyle = {
    font: 'inherit',
    fontSize: 16,
    padding: '6px 8px',
    minHeight: 36,
    border: '1px solid #818cf8',
    borderRadius: 6,
    background: '#fff',
    color: '#111',
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: '100%',
};
/**
 * Click-to-edit value. Tap/click (or Enter/Space when focused) to edit;
 * Enter or blur saves, Esc cancels. Textareas save on Ctrl/Cmd+Enter or blur.
 * With `open`, the input stays open (a row's Edit button opened it) and still saves on blur.
 */
function InlineEdit({ value, onSave, type = 'text', options, placeholder = 'Add…', format, validate, required, disabled, label, style, inputStyle, history, open = false, }) {
    const [editingSelf, setEditing] = (0, react_1.useState)(false);
    const editing = open || editingSelf;
    const [draft, setDraft] = (0, react_1.useState)(() => (open ? toDraft(value, type) : ''));
    const [saving, setSaving] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    const inputRef = (0, react_1.useRef)(null);
    // Esc triggers blur after cancelling — this stops that blur from saving.
    const cancelledRef = (0, react_1.useRef)(false);
    (0, react_1.useEffect)(() => {
        if (editingSelf && !open) {
            inputRef.current?.focus();
            if (type === 'text' || type === 'textarea')
                inputRef.current?.select?.();
        }
    }, [editingSelf, open, type]);
    // A row opening all its fields at once starts each one from the stored value.
    (0, react_1.useEffect)(() => {
        if (open) {
            setDraft(toDraft(value, type));
            setError(null);
        }
        // Only when the row opens — not on every save, which would clobber a field being typed in.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    const start = (e) => {
        e?.stopPropagation();
        if (disabled)
            return;
        cancelledRef.current = false;
        setDraft(toDraft(value, type));
        setError(null);
        setEditing(true);
    };
    const cancel = () => {
        setError(null);
        if (open) {
            // Row stays open; just put this field back to what's saved.
            setDraft(toDraft(value, type));
            return;
        }
        cancelledRef.current = true;
        setEditing(false);
    };
    const commit = async (next = draft) => {
        if (saving || (cancelledRef.current && !open))
            return;
        const trimmed = type === 'textarea' ? next : next.trim();
        if (trimmed === toDraft(value, type)) {
            setEditing(false);
            setError(null);
            return;
        }
        let msg = null;
        if (required && trimmed === '')
            msg = 'Required';
        else if ((type === 'number' || type === 'money') && trimmed !== '' && !Number.isFinite(Number(trimmed))) {
            msg = 'Enter a number';
        }
        else if (validate)
            msg = validate(trimmed);
        if (msg) {
            setError(msg);
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await onSave(trimmed);
            setEditing(false);
            (0, Toast_1.showToast)(label ? `${label} saved ✓` : 'Saved ✓');
        }
        catch (err) {
            setError(err instanceof Error && err.message ? err.message : 'Save failed');
        }
        finally {
            setSaving(false);
        }
    };
    const onKeyDown = (e) => {
        e.stopPropagation();
        if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
        }
        else if (e.key === 'Enter' && (type !== 'textarea' || e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            void commit();
        }
    };
    if (!editing) {
        const empty = value === null || value === undefined || value === '';
        const display = ((0, jsx_runtime_1.jsx)("span", { role: "button", tabIndex: disabled ? -1 : 0, "aria-label": label ? `Edit ${label}` : 'Edit', title: disabled ? undefined : 'Click to edit', onClick: start, onKeyDown: (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    start(e);
                }
            }, style: {
                ...displayStyle,
                ...(disabled ? { cursor: 'default', borderBottomColor: 'transparent' } : null),
                ...style,
            }, children: empty ? ((0, jsx_runtime_1.jsx)("span", { style: { opacity: 0.5 }, children: placeholder })) : format ? (format(value)) : (defaultFormat(value, type, options)) }));
        if (!history || history.length === 0)
            return display;
        return ((0, jsx_runtime_1.jsxs)("span", { style: { display: 'inline-flex', alignItems: 'center', gap: 2, maxWidth: '100%' }, children: [display, (0, jsx_runtime_1.jsx)(FieldHistory_1.FieldHistory, { entries: history, label: label, format: (v) => (v === null ? '' : format ? format(v) : defaultFormat(v, type, options)), onRestore: disabled ? undefined : (v) => onSave(v ?? '') })] }));
    }
    const common = {
        ref: inputRef,
        'aria-label': label,
        'aria-invalid': error ? true : undefined,
        // An open row keeps its inputs usable while one field saves, so focus can move on.
        disabled: saving && !open,
        onKeyDown,
        onClick: (e) => e.stopPropagation(),
        onBlur: () => void commit(),
        style: { ...baseInputStyle, ...(error ? { borderColor: '#ef4444' } : null), ...inputStyle },
    };
    let field;
    if (type === 'textarea') {
        field = (0, jsx_runtime_1.jsx)("textarea", { ...common, rows: 3, value: draft, onChange: (e) => setDraft(e.target.value) });
    }
    else if (type === 'select') {
        field = ((0, jsx_runtime_1.jsx)("select", { ...common, value: draft, onChange: (e) => {
                setDraft(e.target.value);
                void commit(e.target.value);
            }, children: (options ?? []).map((o) => ((0, jsx_runtime_1.jsx)("option", { value: o.value, children: o.label }, o.value))) }));
    }
    else {
        field = ((0, jsx_runtime_1.jsx)("input", { ...common, type: type === 'money' || type === 'number' ? 'number' : type, inputMode: type === 'money' ? 'decimal' : type === 'number' ? 'numeric' : undefined, step: type === 'money' ? '0.01' : undefined, value: draft, onChange: (e) => setDraft(e.target.value) }));
    }
    return ((0, jsx_runtime_1.jsxs)("span", { style: { display: 'inline-flex', flexDirection: 'column', gap: 2, minWidth: 120, maxWidth: '100%', ...style }, children: [(0, jsx_runtime_1.jsxs)("span", { style: { display: 'flex', alignItems: 'center', gap: 6 }, children: [field, saving && (0, jsx_runtime_1.jsx)("span", { "aria-live": "polite", style: { fontSize: 12, opacity: 0.7 }, children: "Saving\u2026" })] }), error && ((0, jsx_runtime_1.jsx)("span", { role: "alert", style: { fontSize: 12, color: '#dc2626' }, children: error }))] }));
}
exports.InlineEdit = InlineEdit;
exports.default = InlineEdit;
